import { create } from 'zustand';
import { createSelectors } from './createSelectors';
import {
  createPaymentMethodIntent,
  deletePaymentMethod,
  listPaymentMethods,
  MethodIntentUpdateRequest,
  paymentMethodExists,
  PaymentMethodExistsResponse,
  PaymentMethodListItem,
  toggleAutopayMethod,
  updatePaymentMethodIntent,
} from '../apis/paymentMethodApi';
import { sleep } from '../utils/asyncUtils';

interface PaymentMethodState {
  clientSecret: string | null;
  setupIntentId: string | null;
  intentIsAutopay: boolean;
  newPaymentMethodId: string | null;
  paymentMethods: PaymentMethodListItem[];
  paymentMethodsLoaded: boolean;
  retrievePaymentMethods: (useCached?: boolean | null) => Promise<PaymentMethodListItem[]>;
  createMethodIntent: () => Promise<void>;
  updateMethodIntent: (request: MethodIntentUpdateRequest) => Promise<void>;
  resetMethodIntent: () => void;
  pollForNewMethod: () => Promise<PaymentMethodExistsResponse>;
  selectAutopayMethod: (paymentMethodId: string) => Promise<void>;
  unselectAutopayMethod: (paymentMethodId: string) => Promise<void>;
  deletePaymentMethod: (paymentMethodId: string) => Promise<void>;
}

const usePaymentMethodStoreBase = create<PaymentMethodState>((set, get) => ({
  clientSecret: null,
  setupIntentId: null,
  intentIsAutopay: false,
  newPaymentMethodId: null,
  paymentMethodsLoaded: false,
  paymentMethods: [],

  retrievePaymentMethods: async (useCached?: boolean | null) => {
    //If it's OK to use cached methods, just exit if method are loaded
    if (useCached && get().paymentMethodsLoaded) {
      return get().paymentMethods;
    }
    const response = await listPaymentMethods();
    set({ paymentMethods: response, paymentMethodsLoaded: true });
    return response;
  },
  createMethodIntent: async () => {
    const response = await createPaymentMethodIntent();
    set({
      clientSecret: response.clientSecret,
      setupIntentId: response.setupIntentId,
      intentIsAutopay: false,
      newPaymentMethodId: null,
      paymentMethodsLoaded: false,
    });
  },
  updateMethodIntent: async (request: MethodIntentUpdateRequest) => {
    await updatePaymentMethodIntent(request);
    set({ intentIsAutopay: request.isAutopay, newPaymentMethodId: request.paymentMethodId, paymentMethodsLoaded: false });
  },
  resetMethodIntent: async () => {
    set({ clientSecret: null, setupIntentId: null, intentIsAutopay: false, newPaymentMethodId: null });
  },
  pollForNewMethod: async () => {
    const paymentMethodId = get().newPaymentMethodId;
    let response: PaymentMethodExistsResponse = { exists: false, isAutopay: false };
    if (paymentMethodId) {
      for (let i = 0; i < 10; i++) {
        await sleep(1000);
        response = await paymentMethodExists(paymentMethodId);
        if (response.exists && response.isAutopay === get().intentIsAutopay) {
          break;
        }
      }
    }
    return response;
  },
  selectAutopayMethod: async (paymentMethodId: string) => {
    await toggleAutopayMethod({ paymentMethodId, isAutopay: true });
  },
  unselectAutopayMethod: async (paymentMethodId: string) => {
    await toggleAutopayMethod({ paymentMethodId, isAutopay: false });
  },
  deletePaymentMethod: async (paymentMethodId: string) => {
    await deletePaymentMethod(paymentMethodId);
  },
}));

export const usePaymentMethodStore = createSelectors(usePaymentMethodStoreBase);
